import { useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import sortBy from "lodash/sortBy";

interface MemberNode {
  node: {
    content: string;
    internalId?: string;
    uuid?: string;
    slug?: string;
    full_slug: string;
  };
}

interface TeamMembersData {
  members: {
    edges: MemberNode[];
  };
}

export interface TeamMemberGroups {
  /** Investment team — Storyblok folder `en/team-members`. */
  team: MemberType[];
  /** Advisors and venture partners — Storyblok folder `en/advisors-partners`. */
  advisors: MemberType[];
}

const byOrder = (members: MemberType[]) =>
  sortBy(members, (el) => (el.order ? parseInt(el.order) : null));

/**
 * Build-time member list used by the Person JSON-LD nodes. Pulls both member
 * folders in one query and partitions them by slug, mirroring the filters used
 * by OurTeamSection and OurAdvisorsPartnersSection.
 */
export function useTeamMembers(): TeamMemberGroups {
  const teamData = useStaticQuery<TeamMembersData>(graphql`
    query StructuredDataMembersQuery {
      members: allStoryblokEntry(
        filter: { field_component: { eq: "data_teamMember" } }
      ) {
        edges {
          node {
            content
            internalId
            uuid
            slug
            full_slug
          }
        }
      }
    }
  `);

  return useMemo(() => {
    const edges = teamData?.members?.edges || [];

    const parse = (match: string) =>
      byOrder(
        edges
          .filter(({ node }) => node.full_slug.includes(match))
          .map(({ node }) => ({
            ...JSON.parse(node.content),
            id: node.internalId,
            uuid: node.uuid,
            slug: node.slug,
          })),
      );

    return {
      team: parse("/team-members/"),
      advisors: parse("/advisors-partners/"),
    };
  }, [teamData]);
}
